import types from '../mutation-types';
import WaCatalogAPI from '../../api/omni/waCatalog';

export const state = {
  catalogs: [],
  products: [],
  collections: [],
  combos: [],
  currentCatalog: null,
  uiFlags: {
    isFetching: false,
    isFetchingProducts: false,
    isFetchingCollections: false,
    isFetchingCombos: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isSyncing: false,
  },
};

export const getters = {
  getCatalogs: _state => _state.catalogs,
  getCurrentCatalog: _state => _state.currentCatalog,
  getProducts: _state => _state.products,
  getCollections: _state => _state.collections,
  getCombos: _state => _state.combos,
  getUIFlags: _state => _state.uiFlags,
  getCatalogById: _state => id =>
    _state.catalogs.find(c => c.id === Number(id)),
  getProductsByCategory: _state => category =>
    _state.products.filter(p => p.category === category),
  getProductsByCollection: _state => collectionId =>
    _state.products.filter(p => p.collection_id === Number(collectionId)),
  getAvailableProducts: _state =>
    _state.products.filter(p => p.availability === 'available' && p.enabled),
};

export const actions = {
  // ============================================
  // Catalogs
  // ============================================
  fetchCatalogs: async ({ commit }) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isFetching: true });
    try {
      const { data } = await WaCatalogAPI.getCatalogs();
      commit(types.SET_WA_CATALOGS, data);
    } catch (error) {
      // ignore
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isFetching: false });
    }
  },

  fetchCatalog: async ({ commit }, catalogId) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isFetching: true });
    try {
      const { data } = await WaCatalogAPI.getCatalog(catalogId);
      commit('SET_CURRENT_CATALOG', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isFetching: false });
    }
  },

  createCatalog: async ({ commit }, catalogData) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isCreating: true });
    try {
      const { data } = await WaCatalogAPI.createCatalog(catalogData);
      commit(types.ADD_WA_CATALOG, data);
      return data;
    } catch (error) {
      const msg = error?.response?.data?.error;
      throw new Error(msg || 'Failed to create catalog');
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isCreating: false });
    }
  },

  updateCatalog: async ({ commit }, { catalogId, ...updateData }) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await WaCatalogAPI.updateCatalog(
        catalogId,
        updateData
      );
      commit(types.EDIT_WA_CATALOG, data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isUpdating: false });
    }
  },

  deleteCatalog: async ({ commit }, catalogId) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isDeleting: true });
    try {
      await WaCatalogAPI.deleteCatalog(catalogId);
      commit(types.DELETE_WA_CATALOG, catalogId);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isDeleting: false });
    }
  },

  syncCatalog: async ({ commit }, { catalogId, direction }) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isSyncing: true });
    try {
      await WaCatalogAPI.syncCatalog(catalogId, direction);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isSyncing: false });
    }
  },

  // ============================================
  // Products
  // ============================================
  fetchProducts: async ({ commit }, { catalogId, params }) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isFetchingProducts: true });
    try {
      const { data } = await WaCatalogAPI.getProducts(catalogId, params);
      commit(types.SET_WA_CATALOG_PRODUCTS, data);
    } catch (error) {
      // ignore
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isFetchingProducts: false });
    }
  },

  createProduct: async ({ commit }, { catalogId, productData }) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isCreating: true });
    try {
      const { data } = await WaCatalogAPI.createProduct(
        catalogId,
        productData
      );
      commit(types.ADD_WA_CATALOG_PRODUCT, data);
      return data;
    } catch (error) {
      const msg = error?.response?.data?.error;
      throw new Error(msg || 'Failed to create product');
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isCreating: false });
    }
  },

  updateProduct: async (
    { commit },
    { catalogId, productId, productData }
  ) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await WaCatalogAPI.updateProduct(
        catalogId,
        productId,
        productData
      );
      commit(types.EDIT_WA_CATALOG_PRODUCT, data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isUpdating: false });
    }
  },

  deleteProduct: async ({ commit }, { catalogId, productId }) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isDeleting: true });
    try {
      await WaCatalogAPI.deleteProduct(catalogId, productId);
      commit(types.DELETE_WA_CATALOG_PRODUCT, productId);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isDeleting: false });
    }
  },

  // ============================================
  // Collections
  // ============================================
  fetchCollections: async ({ commit }, catalogId) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isFetchingCollections: true });
    try {
      const { data } = await WaCatalogAPI.getCollections(catalogId);
      commit(types.SET_WA_CATALOG_COLLECTIONS, data);
    } catch (error) {
      // ignore
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isFetchingCollections: false });
    }
  },

  createCollection: async ({ commit }, { catalogId, collectionData }) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isCreating: true });
    try {
      const { data } = await WaCatalogAPI.createCollection(
        catalogId,
        collectionData
      );
      commit(types.ADD_WA_CATALOG_COLLECTION, data);
      return data;
    } catch (error) {
      const msg = error?.response?.data?.error;
      throw new Error(msg || 'Failed to create collection');
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isCreating: false });
    }
  },

  updateCollection: async (
    { commit },
    { catalogId, collectionId, collectionData }
  ) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await WaCatalogAPI.updateCollection(
        catalogId,
        collectionId,
        collectionData
      );
      commit(types.EDIT_WA_CATALOG_COLLECTION, data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isUpdating: false });
    }
  },

  deleteCollection: async ({ commit }, { catalogId, collectionId }) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isDeleting: true });
    try {
      await WaCatalogAPI.deleteCollection(catalogId, collectionId);
      commit(types.DELETE_WA_CATALOG_COLLECTION, collectionId);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isDeleting: false });
    }
  },

  // ============================================
  // Combos
  // ============================================
  fetchCombos: async ({ commit }, catalogId) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isFetchingCombos: true });
    try {
      const { data } = await WaCatalogAPI.getCombos(catalogId);
      commit(types.SET_WA_CATALOG_COMBOS, data);
    } catch (error) {
      // ignore
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isFetchingCombos: false });
    }
  },

  createCombo: async ({ commit }, { catalogId, comboData }) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isCreating: true });
    try {
      const { data } = await WaCatalogAPI.createCombo(catalogId, comboData);
      commit(types.ADD_WA_CATALOG_COMBO, data);
      return data;
    } catch (error) {
      const msg = error?.response?.data?.error;
      throw new Error(msg || 'Failed to create combo');
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isCreating: false });
    }
  },

  updateCombo: async ({ commit }, { catalogId, comboId, comboData }) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await WaCatalogAPI.updateCombo(
        catalogId,
        comboId,
        comboData
      );
      commit(types.EDIT_WA_CATALOG_COMBO, data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isUpdating: false });
    }
  },

  deleteCombo: async ({ commit }, { catalogId, comboId }) => {
    commit(types.SET_WA_CATALOG_UI_FLAG, { isDeleting: true });
    try {
      await WaCatalogAPI.deleteCombo(catalogId, comboId);
      commit(types.DELETE_WA_CATALOG_COMBO, comboId);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_WA_CATALOG_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [types.SET_WA_CATALOG_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },

  // Catalogs
  [types.SET_WA_CATALOGS](_state, data) {
    _state.catalogs = data;
  },
  [types.ADD_WA_CATALOG](_state, data) {
    _state.catalogs.push(data);
  },
  [types.EDIT_WA_CATALOG](_state, data) {
    const index = _state.catalogs.findIndex(c => c.id === data.id);
    if (index !== -1) {
      _state.catalogs.splice(index, 1, data);
    }
    if (_state.currentCatalog?.id === data.id) {
      _state.currentCatalog = data;
    }
  },
  [types.DELETE_WA_CATALOG](_state, id) {
    _state.catalogs = _state.catalogs.filter(c => c.id !== id);
    if (_state.currentCatalog?.id === id) {
      _state.currentCatalog = null;
    }
  },
  SET_CURRENT_CATALOG(_state, data) {
    _state.currentCatalog = data;
  },

  // Products
  [types.SET_WA_CATALOG_PRODUCTS](_state, data) {
    _state.products = data;
  },
  [types.ADD_WA_CATALOG_PRODUCT](_state, data) {
    _state.products.push(data);
  },
  [types.EDIT_WA_CATALOG_PRODUCT](_state, data) {
    const index = _state.products.findIndex(p => p.id === data.id);
    if (index !== -1) {
      _state.products.splice(index, 1, data);
    }
  },
  [types.DELETE_WA_CATALOG_PRODUCT](_state, id) {
    _state.products = _state.products.filter(p => p.id !== id);
  },

  // Collections
  [types.SET_WA_CATALOG_COLLECTIONS](_state, data) {
    _state.collections = data;
  },
  [types.ADD_WA_CATALOG_COLLECTION](_state, data) {
    _state.collections.push(data);
  },
  [types.EDIT_WA_CATALOG_COLLECTION](_state, data) {
    const index = _state.collections.findIndex(c => c.id === data.id);
    if (index !== -1) {
      _state.collections.splice(index, 1, data);
    }
  },
  [types.DELETE_WA_CATALOG_COLLECTION](_state, id) {
    _state.collections = _state.collections.filter(c => c.id !== id);
  },

  // Combos
  [types.SET_WA_CATALOG_COMBOS](_state, data) {
    _state.combos = data;
  },
  [types.ADD_WA_CATALOG_COMBO](_state, data) {
    _state.combos.push(data);
  },
  [types.EDIT_WA_CATALOG_COMBO](_state, data) {
    const index = _state.combos.findIndex(c => c.id === data.id);
    if (index !== -1) {
      _state.combos.splice(index, 1, data);
    }
  },
  [types.DELETE_WA_CATALOG_COMBO](_state, id) {
    _state.combos = _state.combos.filter(c => c.id !== id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
